import { inject } from '@angular/core';
import { CanDeactivateFn, Router } from '@angular/router';

/**
 * A `CanDeactivate` route guard that preserves query parameters when navigating away
 * from a dialogable route generated via `tuiGenerateDialogableRoute` - aka closing the dialog.
 * This is needed when the dialog is closed and the developer wants to retain certain query params in the URL.
 */
export const PreserveQueryParamsAfterDialogableRouteGuard: CanDeactivateFn<unknown> = (
  _component,
  currentRoute,
  _currentState,
  nextState
) => {
  const router = inject(Router);

  // No data to check, allow navigation
  if (!nextState) {
    return true;
  }

  // Check if we already processed this guard in the current navigation.
  // As the guard is CanDeactivate - it's getting called multiple times.
  // This is to prevent an infinite loop of navigation.
  const currentNavigation = router.getCurrentNavigation();

  // If state includes 'guardProcessed' which was added by this guard, then the guard has already been processed - allow navigation to stop endless loop.
  if (currentNavigation?.extras.state?.['guardProcessed']) {
    return true;
  }

  const preservedQueryParams = currentRoute.queryParams;
  const destinationPath = nextState.url.split('?')[0];

  router.navigate([destinationPath], {
    queryParams: preservedQueryParams,
    state: {
      guardProcessed: true,
    },
  });

  return false;
};
